import React, { useMemo, useState } from 'react';
import { Box, Group, Text, Button, Table, ScrollArea, useMantineTheme, TextInput, Highlight } from '@mantine/core';
import { IconCopy, IconDownload, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

const TariffRawTable = ({ raw, onClose }) => {
  const data = useMemo(() => (raw && raw.results && raw.results[0] ? raw.results[0] : raw) || {}, [raw]);

  const theme = useMantineTheme();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(raw, null, 2));
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(raw, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tariff-${raw?.results?.[0]?.tariffId || 'raw'}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Extract properties (top-level simple key-values excluding 'rates' or arrays)
  const properties = useMemo(() => {
    if (!data || typeof data !== 'object') return {};
    const props = {};
    Object.keys(data).forEach((k) => {
      if (k === 'rates') return;
      const v = data[k];
      if (v === null) props[k] = 'null';
      else if (typeof v === 'object') {
        // skip complex objects for properties view
      } else {
        props[k] = String(v);
      }
    });
    return props;
  }, [data]);

  // Extract rates: support arrays at data.rates or data?.tariff?.rates
  const ratesArray = useMemo(() => {
    const candidates = [data?.rates, data?.tariff?.rates, data?.tariff?.rateGroups];
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
    }
    // fallback: find first array in root
    for (const k of Object.keys(data || {})) {
      if (Array.isArray(data[k])) return data[k];
    }
    return [];
  }, [data]);

  const rateHeaders = useMemo(() => {
    const hdrs = new Set();
    ratesArray.forEach((r) => {
      if (r && typeof r === 'object') Object.keys(r).forEach((k) => hdrs.add(k));
    });
    return Array.from(hdrs);
  }, [ratesArray]);

  // expansion state for nested cells (keys like `rate-<row>-<col>` or `prop-<row>-<col>`)
  const [expandedCells, setExpandedCells] = useState([]);
  const toggleExpanded = (key) => {
    setExpandedCells((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // search state
  const [query, setQuery] = useState('');

  // simple fuzzy/token match: checks if all query tokens appear in the haystack string order-insensitive
  const fuzzyMatch = (haystack = '', q = '') => {
    if (!q) return { matched: true, indexes: [] };
    const hay = String(haystack).toLowerCase();
    const tokens = String(q).toLowerCase().split(/\s+/).filter(Boolean);
    let matched = true;
    tokens.forEach((t) => {
      if (!hay.includes(t)) matched = false;
    });
    return { matched, indexes: [] };
  };

  

  const highlightCell = (text, asPre = false) => {
    const comp = asPre ? 'pre' : 'span';
    const content = String(text ?? '');
    const baseStyle = { margin: 0, fontFamily: asPre ? 'monospace' : undefined, whiteSpace: asPre ? 'pre-wrap' : undefined, color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : undefined };
    if (!query) return <Text component={comp} style={baseStyle}>{content}</Text>;

    // build token regex (escape tokens)
    const tokens = String(query).trim().split(/\s+/).filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (tokens.length === 0) return <Text component={comp} style={baseStyle}>{content}</Text>;
    const re = new RegExp(`(${tokens.join('|')})`, 'ig');
    const parts = content.split(re);

    const highlightStyle = {
      background: theme.colorScheme === 'dark' ? theme.colors.yellow[9] : theme.colors.yellow[1],
      color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : undefined,
      padding: '0 2px',
      borderRadius: 2,
      whiteSpace: asPre ? 'pre-wrap' : undefined,
    };

    return (
      <Text component={comp} style={baseStyle}>
        {parts.map((part, i) => {
          if (!part) return null;
          const lower = part.toLowerCase();
          const isMatch = tokens.some((t) => lower === t.toLowerCase());
          return isMatch ? <span key={i} style={highlightStyle}>{part}</span> : <span key={i}>{part}</span>;
        })}
      </Text>
    );
  };

  const summarizeValue = (val, parentKey = '') => {
    if (Array.isArray(val)) {
      return parentKey ? `${parentKey}[${val.length}]` : `Array[${val.length}]`;
    }
    if (val && typeof val === 'object') {
      return parentKey ? `${parentKey}` : 'Object';
    }
    return String(val);
  };
  const isExpanded = (key) => expandedCells.includes(key);

  const renderNestedContent = (value) => {
    if (Array.isArray(value)) {
      if (value.length === 0) return <Text color="dimmed">Empty array</Text>;
      // array of objects?
      if (value.every((it) => it && typeof it === 'object' && !Array.isArray(it))) {
        const hdrs = Array.from(value.reduce((s, it) => {
          Object.keys(it).forEach((k) => s.add(k));
          return s;
        }, new Set()));
        return (
          <ScrollArea style={{ maxHeight: 240 }}>
            <Table verticalSpacing="xs" fontSize="sm">
              <thead>
                <tr>{hdrs.map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {value.map((row, i) => (
                  <tr key={i}>{hdrs.map((h) => <td key={h} style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{highlightCell(row && row[h] !== undefined ? String(row[h]) : '', true)}</td>)}</tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        );
      }
      // array of primitives or mixed
      return (
        <Box>
          {value.map((v, i) => (
            <Box key={i} mb="xs">{highlightCell(String(v), true)}</Box>
          ))}
        </Box>
      );
    }

    if (value && typeof value === 'object') {
      return (
        <Table verticalSpacing="xs" fontSize="sm">
          <tbody>
            {Object.keys(value).map((k) => (
              <tr key={k}><td style={{ fontWeight: 600, width: 240 }}>{k}</td><td>{highlightCell(String(value[k]), true)}</td></tr>
            ))}
          </tbody>
        </Table>
      );
    }

    return <Text component="span">{String(value)}</Text>;
  };

  // Detect properties array (e.g., data.properties or data.tariff.properties)
  const propertiesArray = useMemo(() => {
    const candidates = [data?.properties, data?.tariff?.properties];
    for (const c of candidates) if (Array.isArray(c)) return c;
    // fallback: find first array with objects that look like properties
    for (const k of Object.keys(data || {})) {
      if (Array.isArray(data[k]) && data[k].length > 0 && typeof data[k][0] === 'object') return data[k];
    }
    return [];
  }, [data]);

  const propertiesArrayHeaders = useMemo(() => {
    const hdrs = new Set();
    propertiesArray.forEach((p) => {
      if (p && typeof p === 'object') Object.keys(p).forEach((k) => hdrs.add(k));
    });
    return Array.from(hdrs);
  }, [propertiesArray]);

  // filter lists based on query (must be after propertiesArray declaration)
  const filteredPropertiesEntries = useMemo(() => {
    const entries = Object.entries(properties);
    if (!query) return entries;
    return entries.filter(([k, v]) => {
      return fuzzyMatch(k, query).matched || fuzzyMatch(String(v), query).matched;
    });
  }, [properties, query]);

  const filteredRates = useMemo(() => {
    if (!query) return ratesArray;
    return ratesArray.filter((r) => {
      if (!r || typeof r !== 'object') return fuzzyMatch(String(r), query).matched;
      // check any field
      return Object.keys(r).some((k) => fuzzyMatch(String(r[k]), query).matched || fuzzyMatch(k, query).matched);
    });
  }, [ratesArray, query]);

  const filteredPropertiesArray = useMemo(() => {
    if (!query) return propertiesArray;
    return propertiesArray.filter((p) => {
      if (!p || typeof p !== 'object') return fuzzyMatch(String(p), query).matched;
      return Object.keys(p).some((k) => fuzzyMatch(String(p[k]), query).matched || fuzzyMatch(k, query).matched);
    });
  }, [propertiesArray, query]);

  return (
    <Box p="sm">
      <Group position="apart" align="center">
        <Text weight={700}>Tariff JSON</Text>
        <Group spacing="xs">
          <Button aria-label="tariff-json-copy" size="xs" variant="outline" onClick={handleCopy} leftIcon={<IconCopy size={16} />}>Copy</Button>
          <Button aria-label="tariff-json-download" size="xs" variant="outline" onClick={handleDownload} leftIcon={<IconDownload size={16} />}>Download</Button>
          <Button aria-label="tariff-json-close" size="xs" variant="subtle" onClick={() => onClose && onClose()} leftIcon={<IconX size={16} />}>Close</Button>
        </Group>
      </Group>

      <Box mt="md">
        <Text weight={600} mb={6}>Properties</Text>
        <Box mb="xs">
          <TextInput placeholder="Search table (fuzzy)" value={query} onChange={(e) => setQuery(e.currentTarget.value)} size="xs" />
        </Box>
        <ScrollArea style={{ height: 220 }}>
          <Table verticalSpacing="xs" fontSize="sm">
            <thead>
              <tr>
                <th style={{ width: 240, position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>Property</th>
                <th style={{ position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredPropertiesEntries.length === 0 && (
                <tr><td colSpan={2}><Text color="dimmed">No matching properties</Text></td></tr>
              )}
              {filteredPropertiesEntries.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ fontWeight: 600 }}>{highlightCell(k, false)}</td>
                  <td>{highlightCell(v, true)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollArea>
      </Box>

      <Box mt="md">
        <Text weight={600} mb={6}>Rates</Text>
        <ScrollArea style={{ height: 360 }}>
          <Table verticalSpacing="xs" fontSize="sm">
            <thead>
              <tr>
                {rateHeaders.length === 0 ? <th style={{ position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>No rates found</th> : rateHeaders.map((h) => <th key={h} style={{ position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filteredRates.length === 0 && (
                <tr><td colSpan={Math.max(1, rateHeaders.length)}><Text color="dimmed">No matching rates</Text></td></tr>
              )}
              {filteredRates.map((r, idx) => (
                <React.Fragment key={idx}>
                  <tr>
                    {rateHeaders.map((h) => {
                      const cellKey = `rate-${idx}-${h}`;
                      const val = r && r[h] !== undefined ? r[h] : '';
                      const expandable = Array.isArray(val) || (val && typeof val === 'object');
                      return (
                        <td key={h} style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                          <Group position="apart" spacing="xs" noWrap>
                            <Box style={{ flex: 1 }}>
                              {highlightCell(!expandable ? String(val) : summarizeValue(val, h), false)}
                            </Box>
                            {expandable && (
                              <Button aria-label={`toggle-rate-cell-${idx}-${h}`} size="xs" variant="subtle" onClick={() => toggleExpanded(cellKey)}>
                                {isExpanded(cellKey) ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                              </Button>
                            )}
                          </Group>
                        </td>
                      );
                    })}
                  </tr>
                  {rateHeaders.map((h) => {
                    const cellKey = `rate-${idx}-${h}`;
                    const val = r && r[h] !== undefined ? r[h] : '';
                    if (!isExpanded(cellKey)) return null;
                    return (
                      <tr key={`${cellKey}-expanded`}>
                        <td colSpan={Math.max(1, rateHeaders.length)} style={{ padding: 8 }}>
                          {renderNestedContent(val)}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </ScrollArea>
      </Box>

      {propertiesArray.length > 0 && (
        <Box mt="md">
          <Text weight={600} mb={6}>Properties Array</Text>
          <ScrollArea style={{ height: 260 }}>
            <Table verticalSpacing="xs" fontSize="sm">
                <thead>
                  <tr>
                    {propertiesArrayHeaders.length === 0 ? <th style={{ position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>No properties found</th> : propertiesArrayHeaders.map((h) => <th key={h} style={{ position: 'sticky', top: 0, background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white, zIndex: 2, borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}` }}>{h}</th>)}
                  </tr>
                </thead>
              <tbody>
                {filteredPropertiesArray.map((p, idx) => (
                  <React.Fragment key={idx}>
                    <tr>
                      {propertiesArrayHeaders.map((h) => {
                        const cellKey = `prop-${idx}-${h}`;
                        const val = p && p[h] !== undefined ? p[h] : '';
                        const expandable = Array.isArray(val) || (val && typeof val === 'object');
                        return (
                          <td key={h} style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                            <Group position="apart" spacing="xs" noWrap>
                              <Box style={{ flex: 1 }}>
                                {highlightCell(!expandable ? String(val) : summarizeValue(val, h), false)}
                              </Box>
                              {expandable && (
                                <Button aria-label={`toggle-property-cell-${idx}-${h}`} size="xs" variant="subtle" onClick={() => toggleExpanded(cellKey)}>
                                  {isExpanded(cellKey) ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                                </Button>
                              )}
                            </Group>
                          </td>
                        );
                      })}
                    </tr>
                    {propertiesArrayHeaders.map((h) => {
                      const cellKey = `prop-${idx}-${h}`;
                      const val = p && p[h] !== undefined ? p[h] : '';
                      if (!isExpanded(cellKey)) return null;
                      return (
                        <tr key={`${cellKey}-expanded`}>
                          <td colSpan={Math.max(1, propertiesArrayHeaders.length)} style={{ padding: 8 }}>
                            {renderNestedContent(val)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        </Box>
      )}
    </Box>
  );
};

export default TariffRawTable;
